/**
 * @file: useLeadMediaUploader.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Local media-attachment uploader shared by the log-activity form and the
 * lead-discussion composer. Mirrors {@link createChatMediaUploader}: an action
 * sheet picks (camera / library) image+video assets, each is pushed through a
 * single {@link MediaFileUploadController} (concurrency + native video
 * compression / thumbnailing for free), and the hosted result is mapped to a
 * `LeadMediaAttachmentInput`.
 *
 * The media service returns dimensions + a generated cover + duration (in
 * SECONDS) on `result.extra`; duration is normalized to MILLISECONDS to match
 * the `LeadMediaAttachment.durationMs` contract.
 */

import {
  captureChatMedia,
  pickChatMedia,
} from '@euler/app/flows/chat/components/composer/attachmentActions';
import { ChatMediaUploadAsset } from '@euler/app/flows/chat/utils/uploadChatMedia';
import { MediaFileUploadController } from '@euler/app/flows/task/functions/task-detail/MediaFileUploadController';
import { UploadedFile } from '@euler/model';
import {
  LeadMediaAttachmentInput,
  LeadMediaKind,
} from '@euler/model/lead/attachment';
import { useServiceFactory } from '@euler/services/factory';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { nanoid } from 'nanoid/non-secure';
import { useCallback, useEffect, useRef, useState } from 'react';
import Toast from 'react-native-root-toast';

/** Storage sub-path for all lead attachments. */
const LEAD_MEDIA_REALM = 'files/leads';

export type LeadMediaUploadStatus = 'uploading' | 'uploaded' | 'error';

export interface LeadMediaUploadItem {
  /** Stable local key (also the React list key). */
  key: string;
  /** The on-device asset uri, shown as the thumbnail while uploading. */
  localUri: string;
  kind: LeadMediaKind;
  /** The asset mime type — lets the preview detect a video reliably. */
  mimeType?: string;
  status: LeadMediaUploadStatus;
  /** Upload progress in [0, 1]; meaningful only while `uploading`. */
  progress: number;
  /** The hosted attachment payload, present once `uploaded`. */
  result?: LeadMediaAttachmentInput;
}

// Map a finished upload to the create payload. The kind is taken from the
// SERVER result, not the picker's guess: the web picker mislabels videos as
// images, but the media service reports the real `type` (`video/*`) and a
// `duration` + `cover` in `extra`. `extra` carries the cover + dimensions
// (+ duration in seconds for video).
function toAttachmentInput(result: UploadedFile): LeadMediaAttachmentInput {
  const extra = (result.extra ?? {}) as {
    width?: number;
    height?: number;
    cover?: string;
    duration?: number;
  };
  const isVideo =
    (result.type ?? '').startsWith('video/') ||
    typeof extra.duration === 'number';
  const kind: LeadMediaKind = isVideo ? 'video' : 'image';
  return {
    kind,
    url: result.url,
    coverUrl: extra.cover || null,
    width: typeof extra.width === 'number' ? extra.width : null,
    height: typeof extra.height === 'number' ? extra.height : null,
    durationMs:
      isVideo && typeof extra.duration === 'number'
        ? Math.round(extra.duration * 1000)
        : null,
    sizeInBytes:
      typeof result.sizeInBytes === 'number' ? result.sizeInBytes : null,
    mimeType: result.type || null,
  };
}

export interface LeadMediaUploader {
  items: LeadMediaUploadItem[];
  /** Open the camera / library action sheet and enqueue the picked assets. */
  pick: () => void;
  /** Drop an item (stops tracking it; an in-flight upload is discarded). */
  remove: (key: string) => void;
  /** Clear every item and abort anything in flight. */
  reset: () => void;
  /** True while any item is still uploading. */
  uploading: boolean;
  /** True when any item failed to upload (submit should be blocked). */
  hasError: boolean;
  /** The hosted payloads, in pick order — what the request should carry. */
  collectUploaded: () => LeadMediaAttachmentInput[];
}

export const useLeadMediaUploader = (): LeadMediaUploader => {
  const { mediaFileService } = useServiceFactory();
  const { showActionSheetWithOptions } = useActionSheet();
  const [items, setItems] = useState<LeadMediaUploadItem[]>([]);
  const controllerRef = useRef<MediaFileUploadController<undefined> | null>(
    null,
  );

  const getController = useCallback(() => {
    if (!controllerRef.current) {
      controllerRef.current = new MediaFileUploadController<undefined>({
        mediaFileService,
      });
    }
    return controllerRef.current;
  }, [mediaFileService]);

  // Tear down the controller (abort in-flight uploads) when the host unmounts.
  useEffect(() => {
    return () => {
      controllerRef.current?.dispose();
      controllerRef.current = null;
    };
  }, []);

  // Patch one item by key; a no-op when the item was already removed, so a
  // late upload callback for a dropped tile is harmless.
  const patch = useCallback(
    (key: string, next: Partial<LeadMediaUploadItem>) => {
      setItems(prev =>
        prev.map(it => (it.key === key ? { ...it, ...next } : it)),
      );
    },
    [],
  );

  const enqueueAsset = useCallback(
    (asset: ChatMediaUploadAsset) => {
      const key = nanoid();
      setItems(prev => [
        ...prev,
        {
          key,
          localUri: asset.uri,
          kind: asset.kind,
          mimeType: asset.mimeType,
          status: 'uploading',
          progress: 0,
        },
      ]);
      getController().enqueue({
        type: asset.kind,
        localFileUri: asset.uri,
        contentType: asset.mimeType,
        parameters: {
          realm: LEAD_MEDIA_REALM,
          cover: true,
          blurhash: true,
          coverSize: 600,
        },
        onSuccess: data => {
          const input = toAttachmentInput(data.result);
          // Correct the item's kind from the server result so the preview shows
          // the cover + play badge (the picker may have guessed wrong).
          patch(key, {
            status: 'uploaded',
            progress: 1,
            kind: input.kind,
            result: input,
          });
        },
        listener: e => {
          if (e.type === 'progress') patch(key, { progress: e.progress });
          else if (e.type === 'compressing')
            patch(key, { status: 'uploading' });
          else if (e.type === 'error') patch(key, { status: 'error' });
          else if (e.type === 'abort') {
            setItems(prev => prev.filter(it => it.key !== key));
          }
        },
      });
    },
    [getController, patch],
  );

  const pick = useCallback(() => {
    const options = ['拍摄', '从相册选择', '取消'];
    showActionSheetWithOptions(
      { options, cancelButtonIndex: 2, title: '添加照片或视频' },
      async selected => {
        try {
          let assets: ChatMediaUploadAsset[] = [];
          if (selected === 0) assets = await captureChatMedia();
          else if (selected === 1) assets = await pickChatMedia();
          else return;
          for (const asset of assets) enqueueAsset(asset);
        } catch (e) {
          Toast.show((e as Error).message, {
            position: Toast.positions.BOTTOM,
          });
        }
      },
    );
  }, [enqueueAsset, showActionSheetWithOptions]);

  const remove = useCallback((key: string) => {
    setItems(prev => prev.filter(it => it.key !== key));
  }, []);

  const reset = useCallback(() => {
    controllerRef.current?.dispose();
    controllerRef.current = null;
    setItems([]);
  }, []);

  const uploading = items.some(it => it.status === 'uploading');
  const hasError = items.some(it => it.status === 'error');

  const collectUploaded = useCallback(
    (): LeadMediaAttachmentInput[] =>
      items
        .filter(it => it.status === 'uploaded' && it.result)
        .map(it => it.result as LeadMediaAttachmentInput),
    [items],
  );

  return {
    items,
    pick,
    remove,
    reset,
    uploading,
    hasError,
    collectUploaded,
  };
};
